/**
 * @fileoverview 고객센터 파일 업로드 API 함수
 * 단일 파일 및 다중 파일 업로드 기능을 제공합니다.
 * multipart/form-data 형식으로 파일을 서버에 전송하고 업로드 결과를 반환합니다.
 */

import { apiInstance } from "@core/api/axios/apiInstance";
import { FileUploadResponse } from "@features/helpdesk/model/postArticlesModel";

// 상수 추출
const FILE_UPLOAD_ENDPOINT = "/question/file/upload";
const MULTIPART_CONTENT_TYPE = "multipart/form-data";

/**
 * 파일 업로드 에러를 처리하고 로깅하는 공통 함수
 *
 * @param {unknown} error - 발생한 에러 객체
 * @param {string} context - 에러 발생 컨텍스트 (로깅용)
 * @throws {unknown} 원본 에러를 다시 throw
 */
const handleFileUploadError = (error: unknown, context: string): void => {
  console.error(`${context}:`, error);
  throw error;
};

/**
 * 단일 파일을 서버에 업로드합니다.
 *
 * FormData를 사용하여 multipart/form-data 형식으로 파일을 전송하고,
 * 서버에서 반환하는 파일 업로드 결과를 반환합니다.
 *
 * @async
 * @param {File} file - 업로드할 파일 객체
 * @returns {Promise<FileUploadResponse>} 파일 업로드 결과 (원본명, 업로드명)
 * @throws {Error} 파일 업로드 실패 시 에러 발생
 */
export const postUploadFile = async (file: File): Promise<FileUploadResponse> => {
  const formData = new FormData();
  formData.append("file", file);

  try {
    const response = await apiInstance.post(FILE_UPLOAD_ENDPOINT, formData, {
      headers: {
        "Content-Type": MULTIPART_CONTENT_TYPE,
      },
    });
    return response.data;
  } catch (error) {
    handleFileUploadError(error, "파일 업로드 실패");
    // TypeScript를 위한 unreachable 코드 (실제로는 위에서 throw됨)
    throw error;
  }
};

/**
 * 여러 파일을 병렬로 서버에 업로드합니다.
 *
 * 각 파일을 개별적으로 업로드하며, Promise.all을 사용하여 모든 업로드가
 * 완료될 때까지 대기합니다. 하나라도 실패하면 전체가 실패합니다.
 *
 * @async
 * @param {File[]} files - 업로드할 파일 배열
 * @returns {Promise<FileUploadResponse[]>} 파일 업로드 결과 배열
 * @throws {Error} 파일 업로드 실패 시 에러 발생
 */
export const postUploadFiles = async (files: File[]): Promise<FileUploadResponse[]> => {
  try {
    const uploadPromises = files.map(postUploadFile);
    return await Promise.all(uploadPromises);
  } catch (error) {
    handleFileUploadError(error, "파일들 업로드 실패");
    // TypeScript를 위한 unreachable 코드 (실제로는 위에서 throw됨)
    throw error;
  }
};
